import { perf } from '../../../shared/lib/perfLogger';
import type { RevitFamily } from '../../../shared/api/revit/types';
import { registerRevitModelCacheInvalidator } from '../../../shared/lib/revit/modelCacheInvalidation';
import { readCacheScopeModelVersion } from '../../../shared/api/grpc/requestedParameterIntent';
import type {
  QuickFillDataScope,
  QuickFillSnapshotShapeOption,
} from '../../../shared/api/grpc/familyClient';
import {
  buildQuickFillDataCacheKey,
  findCompatibleCacheKey,
  normalizeCacheScopeKey,
  normalizeSnapshotShape,
  shouldUseQuickFillBrowserCache,
} from './quickFillCacheIdentity';

export {
  buildCacheKeyPrefix,
  buildQuickFillDataCacheKey,
  findCompatibleCacheKey,
  hasExplicitModelVersionScope,
  normalizeCacheScopeKey,
  normalizeParameterSet,
  normalizeSnapshotShape,
  shouldUseQuickFillBrowserCache,
} from './quickFillCacheIdentity';

export interface QuickFillPipeElement {
  elementId?: string;
  elementName?: string;
  familyName?: string;
  typeName?: string;
  typeId?: string;
  category?: string;
  parameterValues?: Array<{
    parameterId?: string;
    parameterName: string;
    value: string;
    isReadOnly?: boolean;
    isInstance?: boolean;
  }>;
}

interface AvailableParam {
  name: string;
  parameterType: string;
  group: string;
  isInstance: boolean;
}

export interface QuickFillDataSnapshot {
  families: RevitFamily[];
  pipeElements: QuickFillPipeElement[];
  availableParameters: AvailableParam[];
}

const QUICK_FILL_SESSION_CACHE_KEY = 'pipeex.quickFillData.v2:last';
const QUICK_FILL_SESSION_CACHE_TTL_MS = 30 * 60 * 1000;
const QUICK_FILL_SESSION_CACHE_MAX_CHARS = 1_000_000;
const QUICK_FILL_SESSION_CACHE_MAX_ITEMS = 1_000;
const QUICK_FILL_PERSISTENT_CACHE_DB = 'pipeex.quickFillData.cache';
const QUICK_FILL_PERSISTENT_CACHE_STORE = 'snapshots';
const QUICK_FILL_PERSISTENT_CACHE_VERSION = 1;
const QUICK_FILL_PERSISTENT_MARKER_KEY = 'pipeex.quickFillData.v3:keys';
const MAX_PERSISTENT_CACHE_MARKERS = 8;

interface PersistedQuickFillSnapshot {
  cacheKey: string;
  storedAt: number;
  snapshot: QuickFillDataSnapshot;
}

interface QuickFillPersistentMarkerEntry {
  cacheKey: string;
  storedAt: number;
}

export const cloneQuickFillSnapshot = (snapshot: QuickFillDataSnapshot): QuickFillDataSnapshot =>
  typeof structuredClone === 'function'
    ? structuredClone(snapshot)
    : JSON.parse(JSON.stringify(snapshot)) as QuickFillDataSnapshot;

export const normalizeDataScopeValues = (values?: string[]): string[] =>
  [...new Set((Array.isArray(values) ? values : []).map((value) => value.trim()).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right));

export const hasQuickFillDataScope = (dataScope?: QuickFillDataScope): boolean =>
  normalizeDataScopeValues(dataScope?.familyIds).length > 0 ||
  normalizeDataScopeValues(dataScope?.familyNames).length > 0 ||
  normalizeDataScopeValues(dataScope?.familyTypeKeys).length > 0 ||
  normalizeDataScopeValues(dataScope?.elementIds).length > 0 ||
  normalizeDataScopeValues(dataScope?.categoryNames).length > 0;

export const buildDataScopeKey = (dataScope?: QuickFillDataScope): string => {
  if (!hasQuickFillDataScope(dataScope)) {
    return 'scope:all';
  }

  return `scope:${JSON.stringify({
    familyIds: normalizeDataScopeValues(dataScope?.familyIds),
    familyNames: normalizeDataScopeValues(dataScope?.familyNames),
    familyTypeKeys: normalizeDataScopeValues(dataScope?.familyTypeKeys),
    elementIds: normalizeDataScopeValues(dataScope?.elementIds),
    categoryNames: normalizeDataScopeValues(dataScope?.categoryNames),
  })}`;
};

const isFreshCacheTimestamp = (storedAt: unknown): storedAt is number =>
  typeof storedAt === 'number' && Date.now() - storedAt <= QUICK_FILL_SESSION_CACHE_TTL_MS;

export const getQuickFillSnapshotItemCount = (snapshot: QuickFillDataSnapshot): number =>
  snapshot.families.length + snapshot.pipeElements.length + snapshot.availableParameters.length;

const shouldUseQuickFillSessionCache = (snapshot: QuickFillDataSnapshot): boolean =>
  getQuickFillSnapshotItemCount(snapshot) <= QUICK_FILL_SESSION_CACHE_MAX_ITEMS;

const isQuickFillDataSnapshot = (value: unknown): value is QuickFillDataSnapshot => {
  const candidate = value as Partial<QuickFillDataSnapshot> | null;
  return Boolean(
    candidate &&
    Array.isArray(candidate.families) &&
    Array.isArray(candidate.pipeElements) &&
    Array.isArray(candidate.availableParameters)
  );
};

export const readQuickFillSessionSnapshot = (cacheKey: string): QuickFillDataSnapshot | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(QUICK_FILL_SESSION_CACHE_KEY);
    if (!raw) {
      return null;
    }
    if (raw.length > QUICK_FILL_SESSION_CACHE_MAX_CHARS) {
      window.sessionStorage.removeItem(QUICK_FILL_SESSION_CACHE_KEY);
      perf.log('QF-Data', 'Skipped oversized Quick Fill session cache read', {
        rawLength: raw.length,
        maxLength: QUICK_FILL_SESSION_CACHE_MAX_CHARS,
      });
      return null;
    }

    const persisted = JSON.parse(raw) as Partial<PersistedQuickFillSnapshot>;
    if (persisted.cacheKey !== cacheKey || !isQuickFillDataSnapshot(persisted.snapshot)) {
      return null;
    }

    if (!isFreshCacheTimestamp(persisted.storedAt)) {
      window.sessionStorage.removeItem(QUICK_FILL_SESSION_CACHE_KEY);
      return null;
    }

    return cloneQuickFillSnapshot(persisted.snapshot);
  } catch {
    try {
      window.sessionStorage.removeItem(QUICK_FILL_SESSION_CACHE_KEY);
    } catch {
      // Storage cleanup is best-effort only.
    }
    return null;
  }
};

export const writeQuickFillSessionSnapshot = (cacheKey: string, snapshot: QuickFillDataSnapshot): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!shouldUseQuickFillSessionCache(snapshot)) {
    try {
      window.sessionStorage.removeItem(QUICK_FILL_SESSION_CACHE_KEY);
    } catch {
      // Storage cleanup is best-effort only.
    }
    perf.log('QF-Data', 'Skipped oversized Quick Fill session cache write', {
      cacheKey,
      items: getQuickFillSnapshotItemCount(snapshot),
      maxItems: QUICK_FILL_SESSION_CACHE_MAX_ITEMS,
      families: snapshot.families.length,
      elements: snapshot.pipeElements.length,
      availableParameters: snapshot.availableParameters.length,
    });
    return;
  }

  const tPersist = performance.now();
  try {
    const payload: PersistedQuickFillSnapshot = {
      cacheKey,
      storedAt: Date.now(),
      snapshot,
    };
    const serializedPayload = JSON.stringify(payload);
    if (serializedPayload.length > QUICK_FILL_SESSION_CACHE_MAX_CHARS) {
      window.sessionStorage.removeItem(QUICK_FILL_SESSION_CACHE_KEY);
      perf.log('QF-Data', 'Skipped oversized Quick Fill session cache write', {
        cacheKey,
        rawLength: serializedPayload.length,
        maxLength: QUICK_FILL_SESSION_CACHE_MAX_CHARS,
        families: snapshot.families.length,
        elements: snapshot.pipeElements.length,
        availableParameters: snapshot.availableParameters.length,
      });
      return;
    }

    window.sessionStorage.setItem(QUICK_FILL_SESSION_CACHE_KEY, serializedPayload);
    perf.slow('QF-Data', 'persist snapshot to session cache', tPersist, 50, {
      families: snapshot.families.length,
      elements: snapshot.pipeElements.length,
      availableParameters: snapshot.availableParameters.length,
    });
  } catch (error) {
    try {
      window.sessionStorage.removeItem(QUICK_FILL_SESSION_CACHE_KEY);
    } catch {
      // Storage cleanup is best-effort only.
    }
    perf.warn('QF-Data', 'Unable to persist Quick Fill session cache', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

export const clearQuickFillSessionSnapshot = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(QUICK_FILL_SESSION_CACHE_KEY);
  } catch {
    // Storage cleanup is best-effort only.
  }
};

export const readQuickFillPersistentMarkers = (): QuickFillPersistentMarkerEntry[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(QUICK_FILL_PERSISTENT_MARKER_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as { entries?: unknown };
    if (!Array.isArray(parsed.entries)) {
      window.localStorage.removeItem(QUICK_FILL_PERSISTENT_MARKER_KEY);
      return [];
    }

    return parsed.entries
      .filter((entry): entry is QuickFillPersistentMarkerEntry => {
        const candidate = entry as Partial<QuickFillPersistentMarkerEntry> | null;
        return Boolean(
          candidate &&
          typeof candidate.cacheKey === 'string' &&
          isFreshCacheTimestamp(candidate.storedAt)
        );
      })
      .slice(-MAX_PERSISTENT_CACHE_MARKERS);
  } catch {
    try {
      window.localStorage.removeItem(QUICK_FILL_PERSISTENT_MARKER_KEY);
    } catch {
      // Storage cleanup is best-effort only.
    }
    return [];
  }
};

const writeQuickFillPersistentMarkers = (entries: QuickFillPersistentMarkerEntry[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const freshEntries = entries
      .filter((entry) => isFreshCacheTimestamp(entry.storedAt))
      .slice(-MAX_PERSISTENT_CACHE_MARKERS);
    if (freshEntries.length === 0) {
      window.localStorage.removeItem(QUICK_FILL_PERSISTENT_MARKER_KEY);
      return;
    }

    window.localStorage.setItem(
      QUICK_FILL_PERSISTENT_MARKER_KEY,
      JSON.stringify({ entries: freshEntries })
    );
  } catch {
    try {
      window.localStorage.removeItem(QUICK_FILL_PERSISTENT_MARKER_KEY);
    } catch {
      // Storage cleanup is best-effort only.
    }
  }
};

export const hasQuickFillPersistentMarker = (cacheKey: string): boolean => {
  const entries = readQuickFillPersistentMarkers();
  const hasMarker = entries.some((entry) => entry.cacheKey === cacheKey);
  writeQuickFillPersistentMarkers(entries);
  return hasMarker;
};

const rememberQuickFillPersistentMarker = (cacheKey: string, storedAt: number): void => {
  const entries = readQuickFillPersistentMarkers()
    .filter((entry) => entry.cacheKey !== cacheKey);
  entries.push({ cacheKey, storedAt });
  writeQuickFillPersistentMarkers(entries);
};

const forgetQuickFillPersistentMarkers = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(QUICK_FILL_PERSISTENT_MARKER_KEY);
  } catch {
    // Storage cleanup is best-effort only.
  }
};

const openQuickFillPersistentCache = (): Promise<IDBDatabase | null> => {
  if (typeof window === 'undefined' || !window.indexedDB) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = window.indexedDB.open(
        QUICK_FILL_PERSISTENT_CACHE_DB,
        QUICK_FILL_PERSISTENT_CACHE_VERSION
      );
    } catch {
      resolve(null);
      return;
    }
    if (!request) {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(QUICK_FILL_PERSISTENT_CACHE_STORE)) {
        db.createObjectStore(QUICK_FILL_PERSISTENT_CACHE_STORE, { keyPath: 'cacheKey' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
};

export const readQuickFillPersistentSnapshot = async (cacheKey: string): Promise<QuickFillDataSnapshot | null> => {
  if (!hasQuickFillPersistentMarker(cacheKey)) {
    return null;
  }

  const tRead = performance.now();
  const db = await openQuickFillPersistentCache();
  if (!db) {
    return null;
  }

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(QUICK_FILL_PERSISTENT_CACHE_STORE, 'readonly');
      const request = transaction.objectStore(QUICK_FILL_PERSISTENT_CACHE_STORE).get(cacheKey);
      request.onsuccess = () => {
        const persisted = request.result as Partial<PersistedQuickFillSnapshot> | undefined;
        db.close();

        if (
          persisted?.cacheKey !== cacheKey ||
          !isFreshCacheTimestamp(persisted.storedAt) ||
          !isQuickFillDataSnapshot(persisted.snapshot)
        ) {
          resolve(null);
          return;
        }

        perf.slow('QF-Data', 'persistent cache hydration', tRead, 35, {
          cacheKey,
          families: persisted.snapshot.families.length,
          elements: persisted.snapshot.pipeElements.length,
          availableParameters: persisted.snapshot.availableParameters.length,
        });
        resolve(persisted.snapshot);
      };
      request.onerror = () => {
        db.close();
        resolve(null);
      };
    } catch {
      db.close();
      resolve(null);
    }
  });
};

export const writeQuickFillPersistentSnapshot = (cacheKey: string, snapshot: QuickFillDataSnapshot): void => {
  if (typeof window === 'undefined' || !window.indexedDB) {
    return;
  }

  const tPersist = performance.now();
  void (async () => {
    const db = await openQuickFillPersistentCache();
    if (!db) {
      return;
    }

    await new Promise<void>((resolve) => {
      try {
        const storedAt = Date.now();
        const transaction = db.transaction(QUICK_FILL_PERSISTENT_CACHE_STORE, 'readwrite');
        const request = transaction.objectStore(QUICK_FILL_PERSISTENT_CACHE_STORE).put({
          cacheKey,
          storedAt,
          snapshot,
        } satisfies PersistedQuickFillSnapshot);
        request.onsuccess = () => {
          rememberQuickFillPersistentMarker(cacheKey, storedAt);
          db.close();
          perf.slow('QF-Data', 'persist snapshot to persistent cache', tPersist, 75, {
            families: snapshot.families.length,
            elements: snapshot.pipeElements.length,
            availableParameters: snapshot.availableParameters.length,
          });
          resolve();
        };
        request.onerror = () => {
          db.close();
          resolve();
        };
      } catch {
        db.close();
        resolve();
      }
    });
  })().catch((error) => {
    perf.warn('QF-Data', 'Unable to persist Quick Fill persistent cache', {
      error: error instanceof Error ? error.message : String(error),
    });
  });
};

export const clearQuickFillPersistentSnapshots = (reason: string): void => {
  forgetQuickFillPersistentMarkers();

  if (typeof window === 'undefined' || !window.indexedDB) {
    return;
  }

  try {
    const request = window.indexedDB.deleteDatabase(QUICK_FILL_PERSISTENT_CACHE_DB);
    if (!request) {
      return;
    }
    request.onerror = () => {
      perf.warn('QF-Data', 'Unable to clear Quick Fill persistent cache', { reason });
    };
    request.onblocked = () => {
      perf.warn('QF-Data', 'Quick Fill persistent cache clear is blocked by another tab', { reason });
    };
  } catch (error) {
    perf.warn('QF-Data', 'Unable to clear Quick Fill persistent cache', {
      reason,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

const stripQuickFillScopeDisplaySuffix = (value: string): string =>
  value
    .trim()
    .replace(/\s*\(ID:?\s*[A-Za-z0-9_-]+\)\s*$/i, '')
    .trim();

const normalizeQuickFillScopeToken = (value?: string): string =>
  stripQuickFillScopeDisplaySuffix(value ?? '')
    .toLowerCase()
    .replace(/\s*:\s*/g, ':')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeQuickFillCategoryToken = (value?: string): string =>
  (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const matchesScopedQuickFillCategory = (
  categoryName: string | undefined,
  scopedCategories: Set<string>
): boolean =>
  scopedCategories.size === 0 ||
  scopedCategories.has(normalizeQuickFillCategoryToken(categoryName));

const buildFamilyTypeScopeKey = (familyName?: string, typeName?: string): string => {
  const normalizedFamily = normalizeQuickFillScopeToken(familyName);
  const normalizedType = normalizeQuickFillScopeToken(typeName);
  if (!normalizedFamily) {
    return '';
  }

  return normalizedType ? `${normalizedFamily}:${normalizedType}` : normalizedFamily;
};

const matchesScopedFamilyTypeKey = (candidateKey: string, scopedKey: string): boolean => {
  if (!candidateKey || !scopedKey) {
    return false;
  }

  if (scopedKey.includes(':')) {
    return candidateKey === scopedKey;
  }

  return candidateKey === scopedKey || candidateKey.startsWith(`${scopedKey}:`);
};

const shouldKeepScopedFamilyType = (
  family: RevitFamily,
  typeItem: RevitFamily['types'][number],
  scope: QuickFillDataScope,
  resolvedElementFamilyTypeKeys: Set<string>
): boolean => {
  const familyId = normalizeQuickFillScopeToken(family.id);
  const familyName = normalizeQuickFillScopeToken(family.name);
  const typeId = normalizeQuickFillScopeToken(typeItem.id);
  const candidateKey = buildFamilyTypeScopeKey(family.name, typeItem.name);
  const scopedFamilyIds = new Set(normalizeDataScopeValues(scope.familyIds).map(normalizeQuickFillScopeToken));
  const scopedFamilyNames = new Set(normalizeDataScopeValues(scope.familyNames).map(normalizeQuickFillScopeToken));
  const scopedFamilyTypeKeys = normalizeDataScopeValues(scope.familyTypeKeys).map(normalizeQuickFillScopeToken);
  const scopedCategoryNames = new Set(normalizeDataScopeValues(scope.categoryNames).map(normalizeQuickFillCategoryToken));

  if (!matchesScopedQuickFillCategory(family.category, scopedCategoryNames)) {
    return false;
  }

  if (scopedFamilyIds.has(familyId) || scopedFamilyNames.has(familyName)) {
    return true;
  }

  if (scopedFamilyTypeKeys.some((scopedKey) => matchesScopedFamilyTypeKey(candidateKey, scopedKey) || typeId === scopedKey)) {
    return true;
  }

  return Array.from(resolvedElementFamilyTypeKeys).some((scopedKey) =>
    matchesScopedFamilyTypeKey(candidateKey, scopedKey)
  );
};

export const scopeQuickFillSnapshot = (
  snapshot: QuickFillDataSnapshot,
  dataScope?: QuickFillDataScope,
  _parameterNames?: string[]
): QuickFillDataSnapshot => {
  if (!hasQuickFillDataScope(dataScope)) {
    return snapshot;
  }

  const scope = dataScope ?? {};
  const scopedElementIds = new Set(normalizeDataScopeValues(scope.elementIds).map(normalizeQuickFillScopeToken));
  const scopedFamilyIds = new Set(normalizeDataScopeValues(scope.familyIds).map(normalizeQuickFillScopeToken));
  const scopedFamilyNames = new Set(normalizeDataScopeValues(scope.familyNames).map(normalizeQuickFillScopeToken));
  const scopedFamilyTypeKeys = normalizeDataScopeValues(scope.familyTypeKeys).map(normalizeQuickFillScopeToken);
  const scopedCategoryNames = new Set(normalizeDataScopeValues(scope.categoryNames).map(normalizeQuickFillCategoryToken));
  const hasElementScope = scopedElementIds.size > 0;
  const hasFamilyScope =
    scopedFamilyIds.size > 0 || scopedFamilyNames.size > 0 || scopedFamilyTypeKeys.length > 0;
  const hasCategoryScope = scopedCategoryNames.size > 0;

  const scopedPipeElements = snapshot.pipeElements.filter((element) => {
    if (hasElementScope) {
      return scopedElementIds.has(normalizeQuickFillScopeToken(element.elementId));
    }

    if (!matchesScopedQuickFillCategory(element.category, scopedCategoryNames)) {
      return false;
    }

    if (!hasFamilyScope) {
      return true;
    }

    const candidateKey = buildFamilyTypeScopeKey(element.familyName, element.typeName);
    return (scopedFamilyNames.has(normalizeQuickFillScopeToken(element.familyName)) ||
      scopedFamilyTypeKeys.some((scopedKey) => matchesScopedFamilyTypeKey(candidateKey, scopedKey)));
  });
  const resolvedElementFamilyTypeKeys = new Set(
    scopedPipeElements
      .map((element) => buildFamilyTypeScopeKey(element.familyName, element.typeName))
      .filter(Boolean)
  );

  const scopedFamilies = snapshot.families.reduce<RevitFamily[]>((result, family) => {
    const familyId = normalizeQuickFillScopeToken(family.id);
    const familyName = normalizeQuickFillScopeToken(family.name);
    const familyMatched = scopedFamilyIds.has(familyId) || scopedFamilyNames.has(familyName);
    const categoryMatched = matchesScopedQuickFillCategory(family.category, scopedCategoryNames);

    // A wizard category scope selects the family catalog, not only the types
    // currently placed in the model. Element metadata is still filtered below
    // for instance targets, but it must not prune loaded, unplaced sibling types.
    if (hasCategoryScope && categoryMatched && !hasFamilyScope && !hasElementScope) {
      result.push(family);
      return result;
    }

    const matchingTypes = family.types.filter((typeItem) =>
      shouldKeepScopedFamilyType(family, typeItem, scope, resolvedElementFamilyTypeKeys)
    );

    if (matchingTypes.length > 0) {
      result.push({ ...family, types: matchingTypes });
      return result;
    }

    if (
      (familyMatched || (hasCategoryScope && categoryMatched && !hasFamilyScope)) &&
      !hasElementScope
    ) {
      result.push(family);
    }

    return result;
  }, []);

  perf.log('QF-Data', 'Scoped Quick Fill snapshot before cache', {
    originalFamilies: snapshot.families.length,
    scopedFamilies: scopedFamilies.length,
    originalElements: snapshot.pipeElements.length,
    scopedElements: scopedPipeElements.length,
    scopeFamilies: scopedFamilyIds.size + scopedFamilyNames.size,
    scopeTypes: scopedFamilyTypeKeys.length,
    scopeElements: scopedElementIds.size,
    scopeCategories: scopedCategoryNames.size,
  });

  return {
    families: scopedFamilies,
    pipeElements: scopedPipeElements,
    availableParameters: snapshot.availableParameters,
  };
};

export const quickFillResponseCache = new Map<string, QuickFillDataSnapshot>();
export const quickFillInFlightRequests = new Map<string, Promise<QuickFillDataSnapshot>>();

export interface QuickFillCacheProbeDetails {
  cacheKey: string;
  memoryHit: boolean;
  persistentMarkerHit: boolean;
  persistentMarkerCount: number;
  sessionHit: boolean;
  cacheHit: boolean;
}

let lastQuickFillCacheProbeDetails: QuickFillCacheProbeDetails | null = null;

export const clearQuickFillMemoryCaches = (reason: string) => {
  if (quickFillResponseCache.size === 0 && quickFillInFlightRequests.size === 0) {
    return;
  }

  perf.log('QF-Data', 'Clearing Quick Fill memory caches', {
    reason,
    cachedResponses: quickFillResponseCache.size,
    inFlightRequests: quickFillInFlightRequests.size,
  });
  quickFillResponseCache.clear();
  quickFillInFlightRequests.clear();
};

export const clearQuickFillCaches = (reason: string) => {
  clearQuickFillMemoryCaches(reason);
  clearQuickFillSessionSnapshot();
  clearQuickFillPersistentSnapshots(reason);
};

registerRevitModelCacheInvalidator((event) => {
  const requestKeys = [...quickFillResponseCache.keys(), ...quickFillInFlightRequests.keys()];
  const invalidatedModelVersion = event.modelVersion;
  if (event.eventType === 'ModelChanged' && invalidatedModelVersion !== null &&
      requestKeys.some((key) => (readCacheScopeModelVersion(key) ?? -1) >= invalidatedModelVersion)) {
    return;
  }

  clearQuickFillCaches(event.reason);
});

export const __resetQuickFillDataCacheForTests = (): void => {
  clearQuickFillCaches('test-reset');
};

export const __resetQuickFillDataMemoryCacheForTests = (): void => {
  clearQuickFillMemoryCaches('test-reset-memory-only');
};

export const getLastQuickFillDataCacheProbeDetails = (): QuickFillCacheProbeDetails | null =>
  lastQuickFillCacheProbeDetails ? { ...lastQuickFillCacheProbeDetails } : null;

export interface HasQuickFillDataCacheOptions {
  snapshotShape?: QuickFillSnapshotShapeOption;
  cacheScopeKey?: string | null;
  dataScope?: QuickFillDataScope;
}

export const hasQuickFillDataCache = (
  parameterNames?: string[],
  options?: HasQuickFillDataCacheOptions
): boolean => {
  const snapshotShape = normalizeSnapshotShape(options?.snapshotShape);
  const cacheScopeKey = normalizeCacheScopeKey(options?.cacheScopeKey);
  const browserCacheEnabled = shouldUseQuickFillBrowserCache(cacheScopeKey);
  const dataScopeKey = buildDataScopeKey(options?.dataScope);
  const cacheKey = buildQuickFillDataCacheKey(cacheScopeKey, parameterNames, snapshotShape, dataScopeKey);
  const memoryHit = quickFillResponseCache.has(cacheKey);
  const persistentMarkers = browserCacheEnabled ? readQuickFillPersistentMarkers() : [];
  const compatibleMemoryKey = memoryHit
    ? cacheKey
    : findCompatibleCacheKey(
        Array.from(quickFillResponseCache.keys()).map((candidateKey) => ({ cacheKey: candidateKey })),
        cacheScopeKey,
        parameterNames,
        snapshotShape,
        dataScopeKey
      );
  const compatiblePersistentKey = browserCacheEnabled
    ? findCompatibleCacheKey(
        persistentMarkers,
        cacheScopeKey,
        parameterNames,
        snapshotShape,
        dataScopeKey
      )
    : null;
  const persistentMarkerHit = Boolean(compatiblePersistentKey);
  if (browserCacheEnabled) {
    writeQuickFillPersistentMarkers(persistentMarkers);
  }

  if (memoryHit || compatibleMemoryKey || persistentMarkerHit) {
    lastQuickFillCacheProbeDetails = {
      cacheKey: compatibleMemoryKey ?? compatiblePersistentKey ?? cacheKey,
      memoryHit: memoryHit || Boolean(compatibleMemoryKey),
      persistentMarkerHit,
      persistentMarkerCount: persistentMarkers.length,
      sessionHit: false,
      cacheHit: true,
    };
    return true;
  }

  const persistedSnapshot = browserCacheEnabled ? readQuickFillSessionSnapshot(cacheKey) : null;
  if (persistedSnapshot) {
    quickFillResponseCache.set(cacheKey, persistedSnapshot);
    lastQuickFillCacheProbeDetails = {
      cacheKey,
      memoryHit: false,
      persistentMarkerHit: false,
      persistentMarkerCount: persistentMarkers.length,
      sessionHit: true,
      cacheHit: true,
    };
    return true;
  }

  lastQuickFillCacheProbeDetails = {
    cacheKey,
    memoryHit: false,
    persistentMarkerHit: false,
    persistentMarkerCount: persistentMarkers.length,
    sessionHit: false,
    cacheHit: false,
  };
  return false;
};
